import {useEffect, useState} from 'react';
import {cleaningTypeLabel} from '../service/JobTypeService';
import {getDirectoryEntriesByRole, getUsers, mapDirectoryEntriesById} from '../service/UserService';
import {hasWorker} from '../service/jobScheduling';
import {isAdminSession} from '../service/sessionRole';
import {Alert, Form, Table} from 'react-bootstrap';
import AdhocImageUpload from './AdhocImageUpload';
import AdhocImageGallary from './AdhocImageGallary';
import {ASSIGNMENT_MODES, cleanerOptions, isValidDuration} from './propertyEditorUtils';

export function PropertyDurationsFields({durations, onChange}) {
    if (durations.length === 0) return <Alert variant="secondary">No active Duration Types configured.</Alert>;
    return durations.map(duration => (
        <Form.Group key={duration.duration_type_id} className="mb-3" controlId={`property-duration-${duration.duration_type_id}`}>
            <Form.Label>{duration.name}</Form.Label>
            <Form.Control type="number" min={15} max={720} step={15} required
                value={duration.duration_minutes}
                onChange={event => onChange(duration.duration_type_id, event.target.value === '' ? '' : Number(event.target.value))}
                isInvalid={!isValidDuration(duration.duration_minutes)} />
            <Form.Text>Minutes, in 15 minute increments.</Form.Text>
            <Form.Control.Feedback type="invalid">Enter 15–720 minutes in steps of 15.</Form.Control.Feedback>
        </Form.Group>
    ));
}

export function PropertyCleanerFields({rule, skills, directory, region, onChange}) {
    const primary = rule.preferred_worker_id ? String(rule.preferred_worker_id) : '';
    const backup = rule.backup_worker_id ? String(rule.backup_worker_id) : '';
    const mode = rule.assignment_mode === 'FIXED' ? 'FIXED' : 'PREFERRED';
    const options = cleanerOptions(directory, region, [primary, backup]);
    const activeSkills = skills.filter(skill => skill.status === 'ACTIVE');
    const selectedSkills = (rule.skill_ids || []).map(String).filter(id => activeSkills.some(skill => String(skill.id) === id));

    return <>
        <Form.Group className="mb-3" controlId="property-cleaner-primary">
            <Form.Label>Cleaner</Form.Label>
            <Form.Select value={primary} onChange={event => onChange({preferred_worker_id: event.target.value || null,
                backup_worker_id: event.target.value === backup ? null : rule.backup_worker_id})}>
                <option value="">No fixed or preferred cleaner</option>
                {options.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
            </Form.Select>
            {options.length === 0 ? <Form.Text>No active workers in {region || 'this region'}.</Form.Text> : null}
        </Form.Group>
        {primary ? <>
            <Form.Group className="mb-3">
                <Form.Label>Mode</Form.Label>
                {ASSIGNMENT_MODES.map(option => (
                    <Form.Check key={option.value} type="radio" id={`property-cleaner-mode-${option.value}`} name="property-cleaner-mode"
                        value={option.value} checked={mode === option.value} onChange={() => onChange({assignment_mode: option.value})}
                        label={<>{option.label}<span className="d-block text-muted small">{option.help}</span></>} />
                ))}
            </Form.Group>
            <Form.Group className="mb-3" controlId="property-cleaner-backup">
                <Form.Label>Backup cleaner</Form.Label>
                <Form.Select value={backup} onChange={event => onChange({backup_worker_id: event.target.value || null})}>
                    <option value="">None</option>
                    {options.filter(option => option.id !== primary).map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                </Form.Select>
            </Form.Group>
        </> : null}
        <Form.Group className="mb-3" controlId="property-cleaner-skills">
            <Form.Label>Required skills</Form.Label>
            <Form.Select multiple value={selectedSkills}
                onChange={event => onChange({skill_ids: [...event.target.selectedOptions].map(option => Number(option.value))})}>
                {activeSkills.map(skill => <option key={skill.id} value={String(skill.id)}>{skill.name}</option>)}
            </Form.Select>
            {activeSkills.length === 0 ? <Form.Text>No active skills configured.</Form.Text> : null}
        </Form.Group>
    </>;
}

export function PropertyInstructionFields({instruction, type, onChange, onImagesDeleted, onUploadingChange, onDeletingChange, onUploadError}) {
    return <>
        <Form.Group className="mb-3" controlId={`property-instruction-${type}`}>
            <Form.Label>{type} Instructions</Form.Label>
            <Form.Control as="textarea" rows={5} value={instruction.description || ''}
                onChange={event => onChange({description: event.target.value})} />
        </Form.Group>
        {instruction.date ? <p className="text-muted small">Updated {new Date(instruction.date).toLocaleString()}</p> : null}
        <AdhocImageUpload images={instruction.images || []} setImages={images => onChange({images})}
            onUploadingChange={onUploadingChange} onUploadError={onUploadError} />
        <AdhocImageGallary images={instruction.images || []}
            setImages={images => onImagesDeleted ? onImagesDeleted(images) : onChange({images})}
            onBusyChange={onDeletingChange} />
    </>;
}

export function PropertyJobsContent({jobs}) {
    const [isAdmin] = useState(isAdminSession);
    const [workerNames, setWorkerNames] = useState(null);
    const hasJobs = jobs.length > 0;

    useEffect(() => {
        if (!isAdmin || !hasJobs || workerNames !== null) return undefined;
        let active = true;
        getUsers({roles: ['WORKER']})
            .then(async response => {
                if (!response.ok) throw new Error('Unable to load cleaner names.');
                const payload = await response.json();
                if (active) setWorkerNames(mapDirectoryEntriesById(getDirectoryEntriesByRole(payload, 'WORKER')));
            })
            .catch(() => {
                if (active) setWorkerNames({});
            });
        return () => { active = false; };
    }, [isAdmin, hasJobs, workerNames]);

    function cleanerLabel(job) {
        if (!hasWorker(job)) return 'Unassigned';
        if (workerNames === null) return 'Loading…';
        return workerNames[job.cos_job_worker] || `Worker #${job.cos_job_worker}`;
    }

    if (jobs.length === 0) return <Alert variant="info">No jobs found for this property in the ±2 month period.</Alert>;
    return <Table className="property-editor-jobs" hover>
        <caption className="visually-hidden">Property jobs, two months before and after today</caption>
        <thead><tr><th scope="col">Job ID</th><th scope="col">Date</th><th scope="col">Job Type</th><th scope="col">Status</th>
            {isAdmin ? <th scope="col">Cleaner</th> : null}
        </tr></thead>
        <tbody>{jobs.map(job => (
            <tr key={job.cos_job_id || job.job_id}>
                <td><a href={`/manage-job/update-job/${job.cos_job_id}`} target="_blank" rel="noopener noreferrer"
                    onClick={event => event.stopPropagation()}>{job.cos_job_id}</a></td>
                <td>{new Date(job.date).toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'})}</td>
                <td>{cleaningTypeLabel(job)}</td>
                <td>{job.status}</td>
                {isAdmin ? <td className="text-break">{cleanerLabel(job)}</td> : null}
            </tr>
        ))}</tbody>
    </Table>;
}
